import { gql, useQuery } from '@apollo/client'
import values from 'lodash/values'
import { useEffect, useMemo, useState } from 'react'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  ConsoleTicketReceivesQuery,
  ConsoleTicketReceivesQueryVariables,
  EventPickupUserConnectionFragmentDoc,
  EventPickupUsersOptions,
  FetchNewConsoleTicketReceivesQuery,
  FetchNewConsoleTicketReceivesQueryVariables,
  PickupTicketStatus,
  PickupUserBoxFragment,
  PickupUserBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { useAnalytics } from '@/lib/analytics'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { router } =
    useRouter<{ eventId: string; collectionId: string; type: string }>()
  const [filterOptions, setFilterOptions] = useState<EventPickupUsersOptions>({
    type: PickupTicketStatus.All,
    isHistory: !!router.query.type,
  })

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewConsoleTicketReceivesQuery,
    FetchNewConsoleTicketReceivesQueryVariables
  >({
    query: FETCH_NEW_TICKET_RECEIVES,
    variables: {
      eventId: router.query.eventId,
      collectionId: router.query.collectionId,
      options: filterOptions,
    },
    fields: [
      {
        fragmentName: 'PickupUserBox',
        fieldName: 'artistEventPickupUsers',
        fragment: PickupUserBoxFragmentDoc,
      },
    ],
  })

  const { data, loading, refetch, fetchMore } = useQuery<
    ConsoleTicketReceivesQuery,
    ConsoleTicketReceivesQueryVariables
  >(QUERY, {
    variables: {
      eventId: router.query.eventId,
      collectionId: router.query.collectionId,
      colId: router.query.collectionId,
      options: filterOptions,
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  useEffect(() => {
    if (loading || !!data?.artistEventPickupUsers.edges.length) return

    analytics?.track('impression_noData', {
      applicationKey: config.applicationKey,
      artistId: data?.collection?.artist.id,
      eventId: router.query.eventId,
      name: 'pickupSearch',
    })
  }, [loading])

  const eventPickupUsers = useMemo(() => {
    if (!data?.artistEventPickupUsers.edges.length) return []
    return values(
      data.artistEventPickupUsers.edges.reduce(
        (
          acc: Record<
            string,
            {
              pickupUsers: PickupUserBoxFragment[]
              formattedPickupStartEndTime?: string | null
            }
          >,
          { node }
        ) => {
          const key = node.formattedPickupStartEndTime ?? ''
          if (!acc[key]) {
            acc[key] = {
              formattedPickupStartEndTime: node.formattedPickupStartEndTime,
              pickupUsers: [node],
            }
            return acc
          }
          acc[key].pickupUsers.push(node)
          return acc
        },
        {}
      )
    )
  }, [data?.artistEventPickupUsers.edges])

  useEffect(() => {
    setFilterOptions({
      type: PickupTicketStatus.All,
      isHistory: !!router.query.type,
      orderBy: undefined,
      searchTerm: '',
    })
  }, [router.query.type])

  return {
    state: {
      collection: data?.collection ?? null,
      loading,
      type: router.query.type,
      collectionId: router.query.collectionId,
      eventId: router.query.eventId,
      hasNextPage: !!data?.artistEventPickupUsers.pageInfo.hasNextPage,
      artistId: user?.uid ?? '',
      eventPickupUsers,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            eventId: router.query.eventId,
            collectionId: router.query.collectionId,
            colId: router.query.collectionId,
            options: filterOptions,
            pageInput: {
              cursor: data?.artistEventPickupUsers.pageInfo.endCursor,
            },
          },
        })
      },
      async onRefresh(options) {
        setFilterOptions(options)

        await refetch({
          options,
          eventId: router.query.eventId,
          collectionId: router.query.collectionId,
          colId: router.query.collectionId,
        })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleTicketReceives(
    $collectionId: String!
    $colId: ID!
    $eventId: String!
    $options: EventPickupUsersOptions!
    $pageInput: PageInput
  ) {
    collection(id: $colId) {
      id
      title
      artist {
        id
      }
    }
    artistEventPickupUsers(
      collectionId: $collectionId
      eventId: $eventId
      options: $options
      pageInput: $pageInput
    ) {
      ...EventPickupUserConnection
    }
  }
  ${EventPickupUserConnectionFragmentDoc}
`

const FETCH_NEW_TICKET_RECEIVES = gql`
  query FetchNewConsoleTicketReceives(
    $collectionId: String!
    $eventId: String!
    $options: EventPickupUsersOptions!
    $pageInput: PageInput
  ) {
    artistEventPickupUsers(
      collectionId: $collectionId
      eventId: $eventId
      options: $options
      pageInput: $pageInput
    ) {
      ...EventPickupUserConnection
    }
  }
  ${EventPickupUserConnectionFragmentDoc}
`
